import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TablePagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import {
  createAd,
  fetchAdCategories,
  fetchSchemesPage,
  fetchSchemesByTitle,
  uploadYojanaVideo,
  normalizeYojanaListResponse,
} from '../../utils/api';

const MAX_MEDIA_FILE_BYTES = 5 * 1024 * 1024;
const SCHEME_PICKER_PAGE_SIZE = 10;

const emptySubAdd = () => ({
  title: '',
  description: '',
  category: { id: '' },
  mediaUrls: [],
  yojanaId: [],
});

const extractUploadedUrl = (responseData) => {
  if (!responseData) return '';
  if (typeof responseData === 'string') return responseData;
  return (
    responseData?.url ||
    responseData?.location ||
    responseData?.yojanaVideoUrl ||
    responseData?.mediaUrl ||
    ''
  );
};

const loadImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(objectUrl);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to read image dimensions.'));
    };
    img.src = objectUrl;
  });

const loadVideoDimensions = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const dims = { width: video.videoWidth, height: video.videoHeight };
      URL.revokeObjectURL(objectUrl);
      resolve(dims);
    };
    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to read video metadata.'));
    };
    video.src = objectUrl;
  });

/** Normalize category name for rule matching (e.g. "Video Ad" → "VIDEO_AD") */
const normalizeCategoryKey = (name) =>
  String(name || '')
    .trim()
    .replace(/[\s-]+/g, '_')
    .toUpperCase();

/**
 * Aspect rules by ad category (admin / create flow):
 * - VIDEO_AD → 16:9
 * - BANNER → 1.91:1
 * - All other categories → 9:16 (image or video)
 */
const getAdsMediaRuleFromCategoryName = (categoryName) => {
  const key = normalizeCategoryKey(categoryName);
  if (key === 'VIDEO_AD' || key.includes('VIDEO_AD')) {
    return {
      id: 'video_ad',
      targetRatio: 16 / 9,
      hint: '16:9 (landscape). Image or video.',
      recommendedResolution: '1920×1080',
    };
  }
  if (
    key === 'BANNER' ||
    key.startsWith('BANNER_') ||
    key.endsWith('_BANNER') ||
    key.includes('_BANNER_')
  ) {
    return {
      id: 'banner',
      targetRatio: 1.91,
      hint: '1.91:1 (mobile banner). Image or video.',
      recommendedResolution: '1200×628',
    };
  }
  return {
    id: 'portrait',
    targetRatio: 9 / 16,
    hint: '9:16 (portrait). Image or video.',
    recommendedResolution: '1080×1920',
  };
};

const formatMediaRequirements = (rule) => {
  if (!rule) return '';
  return [
    `Aspect ratio: ${rule.hint}`,
    `Recommended resolution: ${rule.recommendedResolution}`,
    'Max file size: 5 MB',
    'Formats: image or video',
  ].join(' · ');
};

const RATIO_TOLERANCE = 0.03;

const validateMediaAspectRatio = (dims, rule) => {
  const { width, height } = dims || {};
  if (!width || !height) {
    return { ok: false, message: 'Unable to determine width and height of the file.' };
  }
  const r = width / height;
  const target = rule.targetRatio;
  const ok = Math.abs(r - target) <= RATIO_TOLERANCE;
  if (ok) return { ok: true, message: '' };
  return {
    ok: false,
    message: `Expected ${rule.hint} Current size ${width}×${height} (ratio ${r.toFixed(3)}, need ~${target.toFixed(3)}).`,
  };
};

/** True when ad category is SCHEME_AD (link sub-ad to one or more yojanas). */
const isSchemeAdCategory = (categoryName) => {
  const key = normalizeCategoryKey(categoryName);
  return key === 'SCHEME_AD' || key.includes('SCHEME_AD');
};

const getSchemeRowId = (scheme) => {
  const raw = scheme?.yojnaId ?? scheme?.yojanaId ?? scheme?.id;
  return raw != null ? Number(raw) : null;
};

const getAdUserId = () => {
  try {
    const raw = localStorage.getItem('adUser');
    if (!raw) return null;
    const user = JSON.parse(raw);
    return user?.userId ?? user?.id ?? null;
  } catch {
    return null;
  }
};

const CreateAd = () => {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [businessTitle, setBusinessTitle] = useState('');
  const [businessContact, setBusinessContact] = useState('');
  const [businessWebsite, setBusinessWebsite] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [subAdds, setSubAdds] = useState([emptySubAdd()]);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  /** Inline red error under the sub-ad upload that triggered it */
  const [mediaFieldError, setMediaFieldError] = useState({ index: null, message: '' });
  const [schemeDialogOpen, setSchemeDialogOpen] = useState(false);
  const [schemeDialogIndex, setSchemeDialogIndex] = useState(null);
  const [schemePickerPage, setSchemePickerPage] = useState(0);
  const [schemePickerTotal, setSchemePickerTotal] = useState(0);
  const [schemePickerItems, setSchemePickerItems] = useState([]);
  const [schemePickerLoading, setSchemePickerLoading] = useState(false);
  const [schemePickerSearch, setSchemePickerSearch] = useState('');
  const [schemePickerSearchApplied, setSchemePickerSearchApplied] = useState('');
  const [schemeLabelById, setSchemeLabelById] = useState({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingCategories(true);
      try {
        const res = await fetchAdCategories();
        if (cancelled) return;
        setCategories(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        console.error('Error fetching ad categories:', err);
        if (!cancelled) setCategories([]);
      } finally {
        if (!cancelled) setLoadingCategories(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadSchemePicker = useCallback(async (pageNo, searchTitle) => {
    setSchemePickerLoading(true);
    try {
      if (searchTitle.trim()) {
        const res = await fetchSchemesByTitle(searchTitle.trim());
        const items = normalizeYojanaListResponse(res?.data);
        setSchemePickerItems(items);
        setSchemePickerTotal(
          res?.data?.page?.totalElements ?? items.length
        );
      } else {
        const res = await fetchSchemesPage({ pageNo, pageSize: SCHEME_PICKER_PAGE_SIZE });
        const payload = res?.data;
        const content = Array.isArray(payload?.content) ? payload.content : [];
        setSchemePickerItems(content);
        setSchemePickerTotal(
          payload?.page?.totalElements ?? payload?.totalElements ?? content.length
        );
      }
    } catch (err) {
      console.error('Error loading yojanas:', err);
      setSchemePickerItems([]);
      setSchemePickerTotal(0);
    } finally {
      setSchemePickerLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!schemeDialogOpen) return undefined;
    let cancelled = false;
    const run = async () => {
      await loadSchemePicker(schemePickerPage, schemePickerSearchApplied);
      if (cancelled) return;
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [schemeDialogOpen, schemePickerPage, schemePickerSearchApplied, loadSchemePicker]);

  const openSchemePicker = (index) => {
    setSchemeDialogIndex(index);
    setSchemePickerPage(0);
    setSchemePickerSearch('');
    setSchemePickerSearchApplied('');
    setSchemeDialogOpen(true);
  };

  const handleSchemePickerSearch = () => {
    setSchemePickerPage(0);
    setSchemePickerSearchApplied(schemePickerSearch);
  };

  const clearSchemePickerSearch = () => {
    setSchemePickerSearch('');
    setSchemePickerSearchApplied('');
    setSchemePickerPage(0);
  };

  const toggleSchemeInPicker = (scheme) => {
    if (schemeDialogIndex == null) return;
    const id = getSchemeRowId(scheme);
    if (id == null) return;

    const title = scheme?.title || scheme?.schemeName || '';
    setSchemeLabelById((prev) => ({
      ...prev,
      [id]: title || prev[id] || `yojnaId ${id}`,
    }));

    const currentIds = Array.isArray(subAdds[schemeDialogIndex]?.yojanaId)
      ? subAdds[schemeDialogIndex].yojanaId
      : [];
    const numericId = Number(id);
    const isSelected = currentIds.some((x) => Number(x) === numericId);
    const nextIds = isSelected
      ? currentIds.filter((x) => Number(x) !== numericId)
      : [...currentIds, numericId];
    updateSubAdd(schemeDialogIndex, 'yojanaId', nextIds);
  };

  const updateSubAdd = (index, field, value) => {
    setSubAdds((prev) => {
      const next = [...prev];
      if (field === 'categoryId') {
        const categoryId = value ? Number(value) : '';
        next[index] = {
          ...next[index],
          category: { id: categoryId },
          mediaUrls: [], // reset uploads when category changes (aspect rules differ)
          yojanaId: [],
        };
      } else if (field === 'mediaUrls') {
        next[index] = { ...next[index], mediaUrls: value };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  };

  const addSubAdd = () => setSubAdds((prev) => [...prev, emptySubAdd()]);
  const removeSubAdd = (index) => {
    setSubAdds((prev) => prev.filter((_, i) => i !== index));
    setSchemeDialogOpen(false);
    setSchemeDialogIndex(null);
  };

  const handleMediaFilesUpload = async (index, files) => {
    const fileArray = Array.from(files || []);
    if (fileArray.length === 0) return;

    setMediaFieldError({ index: null, message: '' });
    setIsUploadingMedia(true);

    try {
      const sub = subAdds[index];
      const categoryId = sub?.category?.id;
      if (!categoryId) {
        setMediaFieldError({
          index,
          message: 'Select an ad category before uploading media.',
        });
        return;
      }

      const cat = categories.find((c) => Number(c.id ?? c.categoryId) === Number(categoryId));
      const categoryName = String(cat?.categoryName ?? cat?.name ?? '');
      const aspectRule = getAdsMediaRuleFromCategoryName(categoryName);

      const validFiles = [];
      const invalidMessages = [];

      for (const file of fileArray) {
        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');
        if (!isVideo && !isImage) {
          invalidMessages.push(`${file.name}: Upload an image or video file.`);
          continue;
        }

        if (file.size > MAX_MEDIA_FILE_BYTES) {
          invalidMessages.push(`${file.name}: File must be 5 MB or smaller.`);
          continue;
        }

        let dims;
        try {
          dims = isVideo ? await loadVideoDimensions(file) : await loadImageDimensions(file);
        } catch (dimErr) {
          invalidMessages.push(`${file.name}: ${dimErr.message || 'Could not read media dimensions.'}`);
          continue;
        }

        const check = validateMediaAspectRatio(dims, aspectRule);
        if (!check.ok) {
          invalidMessages.push(`${file.name}: ${check.message}`);
          continue;
        }

        validFiles.push(file);
      }

      if (validFiles.length === 0) {
        setMediaFieldError({
          index,
          message: invalidMessages.join('\n') || 'No valid media files selected.',
        });
        return;
      }

      const uploadedUrls = [];
      for (const file of validFiles) {
        const uploadResponse = await uploadYojanaVideo(file);
        const responseData = uploadResponse?.data;
        const uploadedUrl = extractUploadedUrl(responseData);
        if (!uploadedUrl) {
          throw new Error('Media upload did not return a valid URL.');
        }
        uploadedUrls.push(uploadedUrl);
      }

      setSubAdds((prev) => {
        const next = [...prev];
        const current = next[index] || emptySubAdd();
        next[index] = {
          ...current,
          mediaUrls: [
            ...(Array.isArray(current.mediaUrls) ? current.mediaUrls : []),
            ...uploadedUrls
          ]
        };
        return next;
      });

      if (invalidMessages.length === 0) {
        setMediaFieldError({ index: null, message: '' });
      }

      if (invalidMessages.length > 0) {
        setMediaFieldError({
          index,
          message: `Some files were skipped:\n${invalidMessages.join('\n')}`,
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Media upload error:', err);
      setMediaFieldError({
        index,
        message:
          err.response?.data?.message ||
          err.message ||
          'Failed to upload media. Please try again.',
      });
    } finally {
      setIsUploadingMedia(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const userId = getAdUserId();
    if (!userId) {
      setError('Session expired. Please log in again.');
      return;
    }

    if (!startDate || !expiryDate) {
      setError('Please select a valid date range (Start Date and Expiry Date).');
      return;
    }

    const subAddsReady = subAdds.filter(
      (s) => s.title.trim() && s.category?.id && Array.isArray(s.mediaUrls) && s.mediaUrls.length > 0
    );

    for (const s of subAddsReady) {
      const cat = categories.find((c) => Number(c.id ?? c.categoryId) === Number(s.category?.id));
      const catName = String(cat?.categoryName ?? cat?.name ?? '');
      if (isSchemeAdCategory(catName)) {
        const ids = Array.isArray(s.yojanaId) ? s.yojanaId : [];
        if (ids.length === 0) {
          setError('For category SCHEME_AD, select at least one yojana (use “Select yojanas”).');
          return;
        }
      }
    }

    const payload = {
      businessTitle: businessTitle.trim(),
      businessContact: Number(businessContact) || 0,
      businessWebsite: businessWebsite.trim() || undefined,
      businessAddress: businessAddress.trim() || undefined,
      title: title.trim(),
      description: description.trim(),
      expiryDate: expiryDate || undefined,
      addUser: { userId: Number(userId) },
      subAdds: subAddsReady.map((s) => {
        const cat = categories.find((c) => Number(c.id ?? c.categoryId) === Number(s.category?.id));
        const catName = String(cat?.categoryName ?? cat?.name ?? '');
        const row = {
          title: s.title.trim(),
          description: (s.description || '').trim(),
          category: { id: Number(s.category?.id) },
          mediaUrls: Array.isArray(s.mediaUrls) ? s.mediaUrls : [],
        };
        if (isSchemeAdCategory(catName)) {
          row.yojanaId = (Array.isArray(s.yojanaId) ? s.yojanaId : [])
            .map((id) => Number(id))
            .filter((n) => Number.isFinite(n));
        }
        return row;
      }),
    };

    if (!payload.title || !payload.businessTitle) {
      setError('Business title and ad title are required.');
      return;
    }

    if (payload.subAdds.length === 0) {
      setError('Add at least one sub-ad with title, category, and uploaded media.');
      return;
    }

    setIsSubmitting(true);
    try {
      await createAd(payload);
      setSuccess('Ad created successfully.');
      setBusinessTitle('');
      setBusinessContact('');
      setBusinessWebsite('');
      setBusinessAddress('');
      setTitle('');
      setDescription('');
      setExpiryDate('');
      setStartDate('');
      setSubAdds([emptySubAdd()]);
    } catch (err) {
      console.error('Create ad error:', err);
      setError(err.response?.data?.message || 'Failed to create ad. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getBillingDays = () => {
    if (!startDate || !expiryDate) return 0;
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${expiryDate}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
    const diffDays = Math.floor((end.getTime() - start.getTime()) / 86400000);
    return diffDays >= 0 ? diffDays + 1 : 0; // inclusive date range
  };

  const estimatedBill = (() => {
    const days = getBillingDays();
    if (days <= 0) return 0;

    const totalPerDay = subAdds.reduce((sum, sub) => {
      const categoryId = sub?.category?.id;
      if (!categoryId) return sum;

      const cat = categories.find((c) => Number(c.id ?? c.categoryId) === Number(categoryId));
      const price = Number(cat?.price);
      return sum + (Number.isFinite(price) ? price : 0);
    }, 0);

    return totalPerDay * days;
  })();

  if (loadingCategories) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Create Ad
      </Typography>
      <Paper elevation={2} sx={{ p: 3, maxWidth: 800 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Typography variant="subtitle1" color="primary" sx={{ mt: 1, mb: 1 }}>
            Business details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Title"
                value={businessTitle}
                onChange={(e) => setBusinessTitle(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Contact"
                type="number"
                value={businessContact}
                onChange={(e) => setBusinessContact(e.target.value)}
                disabled={isSubmitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Website"
                value={businessWebsite}
                onChange={(e) => setBusinessWebsite(e.target.value)}
                disabled={isSubmitting}
                placeholder="www.example.com"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Address"
                value={businessAddress}
                onChange={(e) => setBusinessAddress(e.target.value)}
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" color="primary" sx={{ mt: 2, mb: 1 }}>
            Ad details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ad Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                disabled={isSubmitting}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={isSubmitting}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={isSubmitting}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ pt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Estimated total bill
                </Typography>
                <Typography variant="h6">
                  {getBillingDays() <= 0
                    ? 'Select dates'
                    : estimatedBill > 0
                      ? `INR ${estimatedBill.toFixed(2)}`
                      : 'Select categories'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" color="primary" sx={{ mb: 1 }}>
            Sub-ads (e.g. image / video slots)
          </Typography>
          {subAdds.map((sub, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Sub-ad #{index + 1}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeSubAdd(index)}
                  disabled={isSubmitting || subAdds.length <= 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Title"
                    value={sub.title}
                    onChange={(e) => updateSubAdd(index, 'title', e.target.value)}
                    disabled={isSubmitting}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Category"
                    value={sub.category?.id || ''}
                    onChange={(e) => updateSubAdd(index, 'categoryId', e.target.value)}
                    disabled={isSubmitting}
                  >
                    <MenuItem value="">Select category</MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id ?? cat.categoryId} value={cat.id ?? cat.categoryId}>
                        {cat.categoryName ?? cat.name ?? cat.id}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={sub.description}
                    onChange={(e) => updateSubAdd(index, 'description', e.target.value)}
                    multiline
                    rows={2}
                    disabled={isSubmitting}
                  />
                </Grid>
                {(() => {
                  const categoryId = sub?.category?.id;
                  const cat = categories.find(
                    (c) => Number(c.id ?? c.categoryId) === Number(categoryId)
                  );
                  const categoryName = String(cat?.categoryName ?? cat?.name ?? '');
                  if (!categoryId || !isSchemeAdCategory(categoryName)) return null;
                  const selectedIds = Array.isArray(sub.yojanaId) ? sub.yojanaId : [];
                  const summary =
                    selectedIds.length === 0
                      ? 'No yojanas selected.'
                      : `${selectedIds.length} yojana(s) selected.`;
                  return (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isSubmitting}
                          onClick={() => openSchemePicker(index)}
                        >
                          Select yojanas
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          {summary}
                        </Typography>
                      </Box>
                      {selectedIds.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                          {selectedIds.map((id) => {
                            const title = schemeLabelById[id];
                            const label = title
                              ? `${title} (yojnaId ${id})`
                              : `yojnaId ${id}`;
                            return <Chip key={id} size="small" label={label} />;
                          })}
                        </Box>
                      )}
                    </Grid>
                  );
                })()}
                <Grid item xs={12}>
                  {(() => {
                    const categoryId = sub?.category?.id;
                    const cat = categories.find(
                      (c) => Number(c.id ?? c.categoryId) === Number(categoryId)
                    );
                    const categoryName = String(cat?.categoryName ?? cat?.name ?? '');
                    const aspectRule = categoryId
                      ? getAdsMediaRuleFromCategoryName(categoryName)
                      : null;

                    return (
                      <>
                        <Box sx={{ mb: 0.5 }}>
                          <Button
                            variant="outlined"
                            component="label"
                            disabled={isSubmitting || isUploadingMedia || !categoryId}
                          >
                            Upload media (image or video)
                            <input
                              hidden
                              multiple
                              type="file"
                              accept="image/*,video/*"
                              onChange={(e) => handleMediaFilesUpload(index, e.target.files)}
                            />
                          </Button>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {!categoryId
                              ? 'Select a category to see required size, resolution, and aspect ratio.'
                              : `Category: ${categoryName || '—'}. ${formatMediaRequirements(aspectRule)}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {Array.isArray(sub.mediaUrls) && sub.mediaUrls.length > 0
                              ? `Uploaded: ${sub.mediaUrls.length} file(s)`
                              : 'No media uploaded yet.'}
                          </Typography>
                          {mediaFieldError.index === index && mediaFieldError.message ? (
                            <Typography
                              variant="body2"
                              component="div"
                              sx={{
                                mt: 1,
                                color: 'error.main',
                                whiteSpace: 'pre-line',
                              }}
                              role="alert"
                            >
                              {mediaFieldError.message}
                            </Typography>
                          ) : null}
                        </Box>
                      </>
                    );
                  })()}
                </Grid>
              </Grid>
            </Paper>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addSubAdd}
            disabled={isSubmitting}
            sx={{ mb: 2 }}
          >
            Add sub-ad
          </Button>

          <Box sx={{ mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'Create Ad'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={schemeDialogOpen}
        onClose={() => {
          setSchemeDialogOpen(false);
          setSchemeDialogIndex(null);
        }}
        maxWidth="md"
        fullWidth
        aria-labelledby="scheme-ad-dialog-title"
      >
        <DialogTitle id="scheme-ad-dialog-title">
          Select yojanas (SCHEME_AD)
        </DialogTitle>
        <DialogContent>
          {schemeDialogIndex == null ? null : (
            <>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search by title"
                  value={schemePickerSearch}
                  onChange={(e) => setSchemePickerSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSchemePickerSearch();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={handleSchemePickerSearch}
                  disabled={schemePickerLoading}
                >
                  Search
                </Button>
                {schemePickerSearchApplied ? (
                  <Button variant="text" onClick={clearSchemePickerSearch} disabled={schemePickerLoading}>
                    Clear
                  </Button>
                ) : null}
              </Box>
              {schemePickerLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : schemePickerItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No yojanas found.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {schemePickerItems.map((scheme) => {
                    const id = getSchemeRowId(scheme);
                    if (id == null) return null;
                    const selectedIds = subAdds[schemeDialogIndex]?.yojanaId || [];
                    const checked = selectedIds.some((x) => Number(x) === Number(id));
                    const title = scheme?.title || scheme?.schemeName || `yojnaId ${id}`;
                    return (
                      <ListItem key={id} disablePadding>
                        <ListItemButton onClick={() => toggleSchemeInPicker(scheme)}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                          </ListItemIcon>
                          <ListItemText
                            primary={title}
                            secondary={`yojnaId ${id}${scheme?.yojanaGovernment ? ` · ${scheme.yojanaGovernment}` : ''}`}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
              {!schemePickerSearchApplied && !schemePickerLoading && schemePickerTotal > 0 ? (
                <TablePagination
                  rowsPerPageOptions={[SCHEME_PICKER_PAGE_SIZE]}
                  count={schemePickerTotal}
                  rowsPerPage={SCHEME_PICKER_PAGE_SIZE}
                  page={schemePickerPage}
                  onPageChange={(e, newPage) => setSchemePickerPage(newPage)}
                  onRowsPerPageChange={() => {}}
                />
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSchemeDialogOpen(false);
              setSchemeDialogIndex(null);
            }}
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateAd;
